import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";
import Alert from "src/helpers/alert";
import { register } from "src/api/auth";
import { useDispatch, useSelector } from "react-redux";
import { setAuthUser } from "src/store/slices/auth";
import { Information } from "src/components/login/Information";
import CmtImage from "src/@coremat/CmtImage";
import LockIcon from '@material-ui/icons/Lock';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { usePasswordRequirements } from "src/helpers/passwordRequirements";

const SetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { tenant, pendingUser, loadUser } = useSelector(({ auth }) => auth);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password requirements using helper hook
  const { requirements: currentRequirements, allRequirementsMet } = usePasswordRequirements(password);

  // Get data from pendingUser in Redux
  const { firstName, lastName, email, role: accountType, token } = pendingUser || {};

  // Only redirect if we're sure there's no pending user (after Redux has loaded)
  if (loadUser && !pendingUser) {
    navigate('/signup');
    return null;
  }

  // Show loading state while Redux is initializing
  if (!loadUser) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!allRequirementsMet) {
      Alert.error("Please meet all password requirements");
      return;
    }
    if (password !== confirmPassword) {
      Alert.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      console.log('[SetPassword] Starting registration with data:', {
        firstName,
        lastName,
        role: accountType,
        email,
        hasToken: !!token
      });

      const response = await register({
        firstName,
        lastName,
        role: accountType,
        email,
        password,
        tenantId: tenant.id,
        isEmailVerified: pendingUser.isEmailVerified,
        token: token || null // Include token if it exists
      });

      // The response should include user data and tokens
      // Tokens are automatically handled by the fetch wrapper and stored in cookies
      console.log('[SetPassword] Registration successful', { 
        hasUserData: !!response,
        hasTokens: !!response.tokens,
        isEmailVerified: response.isEmailVerified,
        hasToken: !!token
      });

      // Store user data in Redux
      dispatch(setAuthUser(response));
      
      Alert.success("Registration successful! Welcome to investormatch");
      
      // Navigate to dashboard with replace to prevent going back
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error('[SetPassword] Registration error:', {
        error: err,
        response: err.response?.data,
        status: err.response?.status,
        hasToken: !!token
      });
      
      const errorMessage = err.response?.data?.message || err.message || "Registration failed. Please try again.";
      Alert.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box display="flex" minHeight="100vh" width="100vw" className="bg-white">
      {/* Left column: Information */}
      <Information />
      {/* Right column: Set password form */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        flexGrow={1}
        width="100%"
        minHeight="100vh"
        className="bg-white"
      >
        <Box width="100%" maxWidth={550} mx="auto" display="flex" flexDirection="column" alignItems="flex-start">
          <CmtImage
            alt="InvestorMatch Logo"
            height="100px"
            style={{ margin: "0 auto 32px auto", display: "block", width: "15vw", minWidth: 120, maxWidth: 200 }}
            src={(tenant?.logo || {})["2x"]?.path || "/images/logo-v1.png"}
          />
          <Typography component="h1" variant="h5" align="left" gutterBottom style={{ fontWeight: 600, fontSize: 22, color: "#446475", marginBottom: 24, width: '100%' }}>
            Set Up Your Password
          </Typography>
          <Typography align="left" style={{ marginBottom: 16, width: '100%' }}>
            Create a password to secure your account.
          </Typography>
          <form onSubmit={onSubmit} style={{ width: '100%', maxWidth: 550 }}>
            <Typography variant="body2" style={{ fontWeight: 500, marginBottom: 1 }}>
              Password <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              type={showPassword ? "text" : "password"}
              placeholder="Input Password"
              fullWidth
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                autoComplete: "new-password",
              }}
              value={password}
              onChange={e => setPassword(e.target.value)}
              variant="outlined"
            />

            <Typography variant="body2" style={{ fontWeight: 500, marginBottom: 1 }}>
              Confirm Password <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm Password"
              fullWidth
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                autoComplete: "new-password",
              }}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              variant="outlined"
            />

            {/* Password Requirements Checklist */}
            <Box mt={2} mb={2}>
              <Typography variant="body2" style={{ fontWeight: 500, marginBottom: 8 }}>
                Password Requirements:
              </Typography>
              {currentRequirements.map((requirement, index) => (
                <Box key={index} display="flex" alignItems="center" mb={1}>
                  {requirement.met ? (
                    <CheckCircleIcon style={{ color: '#1976d2', fontSize: 20, marginRight: 8 }} />
                  ) : (
                    <RadioButtonUncheckedIcon style={{ color: '#bdbdbd', fontSize: 20, marginRight: 8 }} />
                  )}
                  <Typography 
                    variant="body2" 
                    style={{ 
                      color: requirement.met ? '#1976d2' : '#757575',
                      fontWeight: requirement.met ? 500 : 400
                    }}
                  >
                    {requirement.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            
            <Box mt={3} position="relative">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={submitting || !allRequirementsMet}
                style={{ height: 48, fontWeight: 600 }}
              >
                Register
              </Button>
              {submitting && <CircularProgress size={24} variant="indeterminate" style={{ position: 'absolute', top: 12, left: '50%', marginLeft: -12 }} />}
            </Box>
          </form>
        </Box>
      </Box>
    </Box>
  );
};

export default SetPassword; 